import React, { useState, useEffect } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Badge } from "../components/ui/badge";

import AffectationsStatsCards from "../components/affectations/AffectationsStatsCards";
import AffectationsActionBar from "../components/affectations/AffectationsActionBar";
import AffectationsFiltersCard from "../components/affectations/AffectationsFiltersCard";
import AffectationsTableCard from "../components/affectations/AffectationsTableCard";
import AffectationsAssignDialog from "../components/affectations/AffectationsAssignDialog";
import AffectationsUnassignDialog from "../components/affectations/AffectationsUnassignDialog";

const JoursFeriesAffectations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [calendarFilter, setCalendarFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(20);

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isUnassignDialogOpen, setIsUnassignDialogOpen] = useState(false);
  const [selectedCalendarId, setSelectedCalendarId] = useState("");
  const [assignmentNotes, setAssignmentNotes] = useState("");

  // Reset page when filters/search change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, departmentFilter, siteFilter, calendarFilter]);

  // Static lookup data (calendars, departments, sites) — fetched once
  const [calendarsQ, departmentsQ, sitesQ] = useQueries({
    queries: [
      {
        queryKey: ['affectation-calendars'],
        queryFn: async () => {
          const r = await apiClient.get("v1/holidays/holiday-calendars", { params: { limit: 500 } });
          return (r.data.calendars || []).filter(c => c.status === "APPROVED");
        },
      },
      {
        queryKey: ['affectation-departments'],
        queryFn: async () => {
          const r = await apiClient.get("v1/organization-chart/");
          const flatten = (depts) => {
            let flat = [];
            depts.forEach(d => {
              flat.push({ id: d.id, name: d.name, code: d.code });
              if (d.enfants?.length > 0) flat = flat.concat(flatten(d.enfants));
            });
            return flat;
          };
          const orgChart = Array.isArray(r.data?.organization_chart) ? r.data.organization_chart : [];
          return flatten(orgChart);
        },
      },
      {
        queryKey: ['affectation-sites'],
        queryFn: () => apiClient.get("v1/sites/").then(r => Array.isArray(r.data) ? r.data : []),
      },
    ],
  });

  // Employees with their calendar assignments (N+1 per employee — keeps existing behaviour)
  const employeesQ = useQuery({
    queryKey: ['affectation-employees', currentPage, departmentFilter, siteFilter, searchTerm, calendarFilter],
    queryFn: async () => {
      const params = { skip: (currentPage - 1) * limit, limit };
      if (searchTerm) params.search = searchTerm;
      if (departmentFilter !== "all") params.department_id = departmentFilter;
      if (siteFilter !== "all") params.site_id = siteFilter;

      const r = await apiClient.get("v1/employees/", { params });
      const rawEmployees = r.data.employees || [];
      const total = r.data.total || 0;

      const enriched = await Promise.all(
        rawEmployees.map(async (emp) => {
          try {
            const calRes = await apiClient.get(`v1/holidays/employee-calendars/employee/${emp.id}`);
            return { ...emp, current_calendar: calRes.data.calendar };
          } catch {
            return { ...emp, current_calendar: null };
          }
        })
      );

      let filtered = enriched;
      if (calendarFilter === "with") filtered = filtered.filter(e => e.current_calendar !== null);
      else if (calendarFilter === "without") filtered = filtered.filter(e => e.current_calendar === null);

      return { employees: filtered, total };
    },
  });

  const calendars = calendarsQ.data ?? [];
  const departments = departmentsQ.data ?? [];
  const sites = sitesQ.data ?? [];
  const employees = employeesQ.data?.employees ?? [];
  const totalEmployees = employeesQ.data?.total ?? 0;
  const loading = employeesQ.isPending;

  const invalidateEmployees = () =>
    queryClient.invalidateQueries({ queryKey: ['affectation-employees'] });

  const handleSelectAll = () => {
    if (selectAll) { setSelectedEmployees([]); setSelectAll(false); }
    else { setSelectedEmployees(employees.map(e => e.id)); setSelectAll(true); }
  };

  const handleSelectEmployee = (id) => {
    setSelectedEmployees(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleOpenAssignModal = () => {
    if (selectedEmployees.length === 0) {
      toast.error(t("holidayAssignments.toasts.selectAtLeastOne"));
      return;
    }
    setSelectedCalendarId("");
    setAssignmentNotes("");
    setIsAssignModalOpen(true);
  };

  const handleAssignCalendar = async () => {
    if (!selectedCalendarId) { toast.error(t("holidayAssignments.toasts.selectCalendar")); return; }
    try {
      await apiClient.post("v1/holidays/employee-calendars/assign", {
        employee_ids: selectedEmployees,
        calendar_id: parseInt(selectedCalendarId),
        notes: assignmentNotes || undefined,
      });
      toast.success(t("holidayAssignments.toasts.assignSuccess", { count: selectedEmployees.length }));
      setIsAssignModalOpen(false);
      setSelectedEmployees([]);
      setSelectAll(false);
      invalidateEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayAssignments.toasts.assignError"));
    }
  };

  const handleUnassignCalendar = async () => {
    try {
      await Promise.all(
        selectedEmployees.map(id =>
          apiClient.delete(`v1/holidays/employee-calendars/employee/${id}/unassign`)
        )
      );
      toast.success(t("holidayAssignments.toasts.unassignSuccess", { count: selectedEmployees.length }));
      setIsUnassignDialogOpen(false);
      setSelectedEmployees([]);
      setSelectAll(false);
      invalidateEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("holidayAssignments.toasts.unassignError"));
    }
  };

  const confirmUnassign = () => {
    if (selectedEmployees.length === 0) {
      toast.error(t("holidayAssignments.toasts.selectAtLeastOne"));
      return;
    }
    const withCal = employees.filter(e => selectedEmployees.includes(e.id) && e.current_calendar !== null);
    if (withCal.length === 0) { toast.error(t("holidayAssignments.toasts.noneHaveCalendar")); return; }
    setIsUnassignDialogOpen(true);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("holidayAssignments.title")}</h1>
          <p className="text-muted-foreground">{t("holidayAssignments.subtitle")}</p>
        </div>
        <Badge variant="outline">{t("holidayAssignments.availableCalendars", { count: calendars.length })}</Badge>
      </div>

      <AffectationsStatsCards employees={employees} />

      <AffectationsActionBar
        selectedEmployees={selectedEmployees}
        handleOpenAssignModal={handleOpenAssignModal}
        confirmUnassign={confirmUnassign}
        onClearSelection={() => { setSelectedEmployees([]); setSelectAll(false); }}
      />

      <AffectationsFiltersCard
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        departmentFilter={departmentFilter} setDepartmentFilter={setDepartmentFilter}
        siteFilter={siteFilter} setSiteFilter={setSiteFilter}
        calendarFilter={calendarFilter} setCalendarFilter={setCalendarFilter}
        departments={departments} sites={sites}
      />

      <AffectationsTableCard
        loading={loading}
        employees={employees}
        selectedEmployees={selectedEmployees}
        selectAll={selectAll}
        handleSelectAll={handleSelectAll}
        handleSelectEmployee={handleSelectEmployee}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalEmployees={totalEmployees}
        limit={limit}
      />

      <AffectationsAssignDialog
        isOpen={isAssignModalOpen}
        setIsOpen={setIsAssignModalOpen}
        selectedEmployeesCount={selectedEmployees.length}
        calendars={calendars}
        selectedCalendarId={selectedCalendarId}
        setSelectedCalendarId={setSelectedCalendarId}
        assignmentNotes={assignmentNotes}
        setAssignmentNotes={setAssignmentNotes}
        handleAssignCalendar={handleAssignCalendar}
      />

      <AffectationsUnassignDialog
        isOpen={isUnassignDialogOpen}
        setIsOpen={setIsUnassignDialogOpen}
        selectedEmployeesCount={selectedEmployees.length}
        handleUnassignCalendar={handleUnassignCalendar}
      />
    </div>
  );
};

export default JoursFeriesAffectations;
